/**
 * Verifica si un plan está vigente
 * PRIORIZA fechaVencimiento si existe, fallback a cálculo manual
 * @param {Object} objeto - El objeto con plan, fechaActivacion y duracionPlanMeses
 * @returns {boolean} - true si está vigente, false si expiró
 */
export const isPlanVigente = (objeto) => {
  // ✅ NUEVO: Plan Light siempre está vigente
  if (objeto.plan === 'light') {
    return true;
  }
  
  // ✅ NUEVA LÓGICA: Priorizar fechaVencimiento
  if (objeto.fechaVencimiento) {
    const fechaVencimiento = objeto.fechaVencimiento instanceof Date 
      ? objeto.fechaVencimiento 
      : new Date(objeto.fechaVencimiento);
    
    return new Date() < fechaVencimiento;
  }
  
  // ⚠️ FALLBACK: Calcular manualmente para objetos antiguos
  if (!objeto.fechaActivacion || !objeto.duracionPlanMeses) {
    return false;
  }
  
  const fechaActivacion = objeto.fechaActivacion instanceof Date 
    ? objeto.fechaActivacion 
    : new Date(objeto.fechaActivacion);
    
  const diasVigencia = objeto.duracionPlanMeses * 30;
  const fechaExpiracion = new Date(fechaActivacion);
  fechaExpiracion.setDate(fechaExpiracion.getDate() + diasVigencia);
  
  return new Date() < fechaExpiracion;
};

/**
 * Calcula los días restantes del plan
 * PRIORIZA fechaVencimiento si existe
 * @param {Object} objeto - El objeto con plan
 * @returns {number|null} - Días restantes o null si no aplica
 */
export const getDiasRestantes = (objeto) => {
  // ✅ NUEVO: Plan Light no tiene días restantes
  if (objeto.plan === 'light') {
    return null;
  }
  
  let fechaExpiracion;
  
  // ✅ NUEVA LÓGICA: Priorizar fechaVencimiento
  if (objeto.fechaVencimiento) {
    fechaExpiracion = objeto.fechaVencimiento instanceof Date 
      ? objeto.fechaVencimiento 
      : new Date(objeto.fechaVencimiento);
  } 
  // ⚠️ FALLBACK: Calcular manualmente
  else if (objeto.fechaActivacion && objeto.duracionPlanMeses) {
    const fechaActivacion = objeto.fechaActivacion instanceof Date 
      ? objeto.fechaActivacion 
      : new Date(objeto.fechaActivacion);
      
    const diasVigencia = objeto.duracionPlanMeses * 30;
    fechaExpiracion = new Date(fechaActivacion);
    fechaExpiracion.setDate(fechaExpiracion.getDate() + diasVigencia);
  } else {
    return null;
  }
  
  const hoy = new Date();
  const diferencia = fechaExpiracion - hoy;
  const diasRestantes = Math.ceil(diferencia / (1000 * 60 * 60 * 24));
  
  return diasRestantes > 0 ? diasRestantes : 0;
};

/**
 * Determina si mostrar aviso de renovación
 * @param {Object} objeto - El objeto con plan
 * @returns {boolean} - true si debe mostrar aviso
 */
export const debeMostrarAvisoRenovacion = (objeto) => {
  // ✅ NUEVO: Plan Light nunca muestra aviso de renovación
  if (objeto.plan === 'light') {
    return false;
  }
  
  const dias = getDiasRestantes(objeto);
  return dias !== null && dias <= 30 && dias > 0;
};

/**
 * Obtiene la fecha de vencimiento formateada
 * @param {Object} objeto - El objeto con plan
 * @returns {string|null} - Fecha formateada o null
 */
export const getFechaVencimientoFormateada = (objeto) => {
  // ✅ NUEVO: Plan Light no tiene fecha de vencimiento
  if (objeto.plan === 'light') {
    return null;
  }
  
  let fechaVencimiento;
  
  if (objeto.fechaVencimiento) {
    fechaVencimiento = objeto.fechaVencimiento instanceof Date 
      ? objeto.fechaVencimiento 
      : new Date(objeto.fechaVencimiento);
  } else if (objeto.fechaActivacion && objeto.duracionPlanMeses) {
    const fechaActivacion = objeto.fechaActivacion instanceof Date 
      ? objeto.fechaActivacion 
      : new Date(objeto.fechaActivacion);
      
    fechaVencimiento = new Date(fechaActivacion);
    fechaVencimiento.setMonth(fechaVencimiento.getMonth() + objeto.duracionPlanMeses);
  } else {
    return null;
  }
  
  return fechaVencimiento.toLocaleDateString('es-MX', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
};

/**
 * ✅ NUEVO: Obtiene el plan efectivo considerando vencimiento
 * REGLA DE NEGOCIO: Plan vencido = funcionalidades de Light
 * @param {Object} objeto - El objeto con plan y fechaVencimiento
 * @returns {string} - 'light', 'standard', o 'premium'
 */
export const getPlanEfectivo = (objeto) => {
  // Light siempre es light (nunca expira)
  if (objeto.plan === 'light') {
    return 'light';
  }

  // Si el plan está vencido, tratar como Light
  if (!isPlanVigente(objeto)) {
    return 'light';
  }

  // Plan vigente, retornar el plan original
  return objeto.plan;
};

/**
 * ✅ NUEVO: Obtiene el límite de recordatorios según plan y tipo
 * MASCOTAS: Light=0, Standard=5, Premium=10
 * OBJETOS: Light=0, Premium=5
 * @param {Object} objeto - El objeto con plan y tipo
 * @returns {number} - Límite de recordatorios
 */
export const getLimiteRecordatorios = (objeto) => {
  const planEfectivo = getPlanEfectivo(objeto);
  const tipo = objeto.tipo || 'mascota';

  if (tipo === 'mascota') {
    // Mascotas: Light=0, Standard=5, Premium=10
    if (planEfectivo === 'premium') return 10;
    if (planEfectivo === 'standard') return 5;
    return 0; // light
  } else {
    // Objetos (llaveros): Light=0, Premium=5
    if (planEfectivo === 'premium') return 5;
    return 0; // light
  }
};

/**
 * ✅ NUEVO: Verifica si el usuario puede usar una funcionalidad
 * Aplica la regla de "vencido = Light"
 * @param {Object} objeto - El objeto con plan
 * @param {string} funcionalidad - Nombre de la funcionalidad
 * @returns {boolean} - true si tiene acceso
 */
export const tieneAccesoA = (objeto, funcionalidad) => {
  const planEfectivo = getPlanEfectivo(objeto);
  const tipo = objeto.tipo || 'mascota';

  // Funcionalidades por plan para MASCOTAS
  // Actualizado: recompensa disponible en todos los planes
  // Actualizado: ubicacionEscaneo disponible desde Standard (solo modo perdido)
  // Actualizado: expediente básico (manual, sin IA) disponible desde Standard
  // Premium exclusivo: extraccionIA, veterinarios, citas, actividadPersonalizada, tratamientos
  const accesoMascotas = {
    light: ['recuperacion', 'editar', 'reportarPerdido', 'pin', 'recompensa'],
    standard: ['recuperacion', 'editar', 'reportarPerdido', 'pin', 'bloquear', 'recordatorios', 'asistenteIA', 'proveedores', 'recompensa', 'ubicacionEscaneo', 'expediente'],
    premium: ['recuperacion', 'editar', 'reportarPerdido', 'pin', 'bloquear', 'recordatorios', 'asistenteIA', 'proveedores', 'veterinarios', 'expediente', 'citas', 'actividadPersonalizada', 'recompensa', 'ubicacionEscaneo', 'extraccionIA', 'tratamientos']
  };

  // Funcionalidades por plan para OBJETOS
  const accesoObjetos = {
    light: ['recuperacion', 'editar', 'reportarPerdido', 'pin'],
    premium: ['recuperacion', 'editar', 'reportarPerdido', 'pin', 'asistenteIA', 'recompensa', 'bloquear', 'recordatorios']
  };

  const acceso = tipo === 'mascota' ? accesoMascotas : accesoObjetos;
  const funcionalidadesPermitidas = acceso[planEfectivo] || acceso.light;

  return funcionalidadesPermitidas.includes(funcionalidad);
};